import { Button, Image, Upload, Modal, Spin, Tooltip } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { CameraOutlined, DeleteOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import { fetchImage } from "./downloadUploadImage";
import getCroppedImg from "../../components/applications/cropImage";
import { useSelector } from "react-redux";

function UploadCropImage({
  uploadFileFunction,
  setUploadedFileUrl,
  initialUploadUrl,
  name,
  listType,
  maxCount,
  buttonLabel,
  icon,
  isMultiple = false,
  setDeletedImage,
  trustId,
  isCropping = false,
  serviceName = "application",
  cropAspect = 4 / 3,
  ...props
}) {
  const userDetails = useSelector((state) => state.auth);
  const user = userDetails ? userDetails.user : null;
  const [fileList, setFileList] = useState([]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppingModalVisible, setCroppingModalVisible] = useState(false);
  const [fileToCrop, setFileToCrop] = useState(null);
  const [fileToCropUrl, setFileToCropUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Helper: Check if initialUploadUrl changed
  const serializeUploadUrls = (urls) =>
    JSON.stringify(urls?.map((item) => item?.fileName).sort() || []);

  const [lastSerializedUrl, setLastSerializedUrl] = useState("");

  useEffect(() => {
    const fetchInitialImages = async () => {
      const serialized = serializeUploadUrls(initialUploadUrl);
      if (serialized === lastSerializedUrl) return;

      setLastSerializedUrl(serialized);

      if (initialUploadUrl && initialUploadUrl.length > 0) {
        const fetched = await Promise.all(
          initialUploadUrl.map(async (item, index) => {
            const url = await fetchImage(item.fileName);
            return {
              uid: `${index}`,
              name: name,
              status: "done",
              url,
              fileName: item.fileName,
            };
          })
        );
        setFileList(fetched);
        setUploadedFileUrl(fetched);
      } else {
        // Reset if empty (no image for current record)
        setFileList([]);
        setUploadedFileUrl([]);
      }
    };

    fetchInitialImages();
  }, [initialUploadUrl, name]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Cleanup blob URL when modal closes or component unmounts
  useEffect(() => {
    return () => {
      if (fileToCropUrl) {
        URL.revokeObjectURL(fileToCropUrl);
      }
    };
  }, [fileToCropUrl]);

  const getCroppedFileAndUpload = async () => {
    if (!croppedAreaPixels || !fileToCrop) return;
    setIsUploading(true);

    try {
      const croppedBlob = await getCroppedImg(fileToCropUrl, croppedAreaPixels);

      const croppedFile = new File([croppedBlob], fileToCrop.name, {
        type: fileToCrop.type,
      });

      await handleFileUpload(croppedFile);
    } catch (err) {
      console.error("Cropping failed:", err);
    } finally {
      setCroppingModalVisible(false);
      setFileToCrop(null);
      setFileToCropUrl(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("trustId", trustId);
    formData.append(
      "path",
      [serviceName, user?.id, file?.name].filter(Boolean).join("/")
    );

    const response = await uploadFileFunction(formData, trustId);
    if (response?.data?.result) {
      const filePath = response.data.result.filePath;
      const url = await fetchImage(filePath);

      const newItem = {
        uid: `${Date.now()}`,
        name: name,
        status: "done",
        url,
        fileName: filePath,
      };

      const newList = isMultiple ? [...fileList, newItem] : [newItem];
      setFileList(newList);
      setUploadedFileUrl(newList);
    }
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      if (isCropping) {
        const objectUrl = URL.createObjectURL(file);
        setFileToCropUrl(objectUrl);
        setFileToCrop(file);
        setCroppingModalVisible(true);
      } else {
        await handleFileUpload(file);
        onSuccess("ok");
      }
    } catch (err) {
      console.error("Upload error:", err);
      onError(err);
    }
  };

  const handleRemove = (file) => {
    const updated = fileList.filter((item) => item.uid !== file.uid);
    setFileList(updated);
    setUploadedFileUrl(updated);

    if (setDeletedImage) {
      setDeletedImage(file.fileName || file.name);
    }
  };

  const handleModalCancel = () => {
    if (!isUploading) {
      if (fileToCropUrl) {
        URL.revokeObjectURL(fileToCropUrl);
      }
      setCroppingModalVisible(false);
      setFileToCrop(null);
      setFileToCropUrl(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  return (
    <>
      <Upload
        name={name}
        listType={listType}
        fileList={fileList}
        customRequest={customRequest}
        onRemove={handleRemove}
        multiple={isMultiple}
        maxCount={maxCount}
        showUploadList={false}
        {...props}
      >
        <div
          style={{
            width: 120,
            height: 150,
            border: "1px dashed #ccc",
            borderRadius: 6,
            position: "relative",
            textAlign: "center",
            overflow: "hidden",
            backgroundColor: "#fafafa",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {fileList.length > 0 ? (
            <>
              <img
                src={fileList[0].url}
                alt="Uploaded"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  padding: "4px 0",
                }}
              >
                <Tooltip title="Change Photo">
                  <CameraOutlined style={{ color: "white", fontSize: 16 }} />
                </Tooltip>
                <Tooltip title="Remove Photo">
                  <DeleteOutlined
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering Upload
                      handleRemove(fileList[0]);
                    }}
                    style={{
                      color: "white",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              </div>
            </>
          ) : (
            <>
              <CameraOutlined style={{ fontSize: 20, color: "#bbb" }} />
              <div
                style={{
                  fontSize: 12,
                  color: "#bbb",
                  marginTop: 8,
                }}
              >
                Upload Photo
              </div>
            </>
          )}
        </div>
      </Upload>

      {/* {fileList.map((file) => (
        <div key={file.uid} className="previewImages">
          <Image
            style={{ height: "55px", width: "50px" }}
            src={file.url}
            alt={file.name}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(file)}
          />
        </div>
      ))} */}

      <Modal
        open={croppingModalVisible}
        onCancel={handleModalCancel}
        onOk={getCroppedFileAndUpload}
        okText={isUploading ? <Spin size="small" /> : "Crop & Upload"}
        cancelText="Cancel"
        okButtonProps={{ disabled: isUploading, type: "primary" }}
        cancelButtonProps={{ disabled: isUploading }}
        width={500}
        maskClosable={!isUploading}
        keyboard={!isUploading}
        centered
        // style={{
        //   padding: 20,
        //   backgroundColor: "#f9f9f9",
        //   borderRadius: 8,
        // }}
      >
        <div style={{ marginBottom: 16 }}>
          <h3 className="commonFont commonFontFamily">Adjust & Crop Image</h3>
          <p className="commonSmallFont commonFontFamily">
            Move and zoom the image to fit the frame.
          </p>
        </div>

        {fileToCrop && (
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 300,
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              overflow: "hidden",
              backgroundColor: "#000",
            }}
          >
            <Cropper
              image={fileToCropUrl}
              crop={crop}
              zoom={zoom}
              aspect={cropAspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 4 }}>Zoom:</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
            disabled={isUploading}
          />
        </div>
      </Modal>
    </>
  );
}

export default UploadCropImage;
